// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiStorageObject, StorageObjectList, StorageSearchFilters, StoragePermission, StorageWritePermission } from '../../types/storage';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './Storage.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const PageFirstIcon = () => (
  <img src="/static/svg/page-first.svg" alt="First page" width="20" height="20" />
);

const PageNextIcon = () => (
  <img src="/static/svg/page-next.svg" alt="Next page" width="20" height="20" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const Storage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [storageObjects, setStorageObjects] = useState<ApiStorageObject[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<StorageSearchFilters>({
    collection: '',
    key: '',
    user_id: '',
  });

  const sessionRole = authService.getSessionRole();

  // Initialize filters from URL params
  useEffect(() => {
    const collection = searchParams.get('collection') || '';
    const key = searchParams.get('key') || '';
    const userId = searchParams.get('user_id') || '';
    const cursor = searchParams.get('cursor') || '';

    setSearchFilters({
      collection,
      key,
      user_id: userId,
    });
    setNextCursor(cursor);

    if (cursor || collection || key || userId) {
      loadStorageObjects(cursor, collection, key, userId);
    } else {
      loadStorageObjects();
    }
  }, []);

  const loadStorageObjects = useCallback(async (
    cursor?: string,
    collection?: string,
    key?: string,
    userId?: string
  ) => {
    setLoading(true);
    setError('');

    try {
      const result: StorageObjectList = await consoleService.listStorageObjects(
        '',
        collection || searchFilters.collection,
        key || searchFilters.key,
        userId || searchFilters.user_id,
        cursor
      );

      setStorageObjects(result.objects || []);
      setNextCursor(result.cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load storage objects');
    } finally {
      setLoading(false);
    }
  }, [searchFilters]);

  // Refresh data when component becomes visible again (e.g., returning from edit)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Refresh the current view when page becomes visible
        loadStorageObjects(nextCursor, searchFilters.collection, searchFilters.key, searchFilters.user_id);
      }
    };

    // Listen for browser navigation events (back/forward)
    const handlePopState = () => {
      // Refresh when user navigates back to this page
      setTimeout(() => {
        loadStorageObjects(nextCursor, searchFilters.collection, searchFilters.key, searchFilters.user_id);
      }, 100);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [nextCursor, searchFilters, loadStorageObjects]);

  const handleSearch = useCallback((direction: number = 0) => {
    let cursor = '';
    switch (direction) {
      case 0:
        cursor = '';
        break;
      case 1:
        cursor = nextCursor;
        break;
    }
    
    // Update URL params
    const params = new URLSearchParams();
    if (searchFilters.collection) params.set('collection', searchFilters.collection);
    if (searchFilters.key) params.set('key', searchFilters.key);
    if (searchFilters.user_id) params.set('user_id', searchFilters.user_id);
    if (cursor) params.set('cursor', cursor);
    
    setSearchParams(params);
    loadStorageObjects(cursor, searchFilters.collection, searchFilters.key, searchFilters.user_id);
  }, [searchFilters, nextCursor, setSearchParams, loadStorageObjects]);

  const handleFilterChange = (field: keyof StorageSearchFilters, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(0);
  };

  const handleRefresh = () => {
    loadStorageObjects(nextCursor, searchFilters.collection, searchFilters.key, searchFilters.user_id);
  };

  const handleDeleteStorageObject = (storageObject: ApiStorageObject, index: number) => {
    openConfirmDialog(
      'Delete Storage Object',
      `Are you sure you want to delete the storage object "${storageObject.collection}/${storageObject.key}" for user "${storageObject.user_id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteStorageObjects('', {
            object_ids: [{
              collection: storageObject.collection,
              key: storageObject.key,
              user_id: storageObject.user_id,
              version: storageObject.version
            }]
          });
          setStorageObjects(prev => prev.filter((_, i) => i !== index));
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete storage object');
        }
      }
    );
  };

  const handleViewStorageObject = (storageObject: ApiStorageObject) => {
    navigate(`/storage/${encodeURIComponent(storageObject.collection)}/${encodeURIComponent(storageObject.key)}/${encodeURIComponent(storageObject.user_id)}`);
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getPermissionText = (permission: number, isWrite: boolean = false) => {
    if (isWrite) {
      switch (permission) {
        case StorageWritePermission.NO_WRITE:
          return 'No Write';
        case StorageWritePermission.OWNER_WRITE:
          return 'Owner Write';
        default:
          return 'Unknown';
      }
    } else {
      switch (permission) {
        case StoragePermission.NO_READ:
          return 'No Read';
        case StoragePermission.OWNER_READ:
          return 'Owner Read';
        case StoragePermission.PUBLIC_READ:
          return 'Public Read';
        default:
          return 'Unknown';
      }
    }
  };

  return (
    <div className="storage-container">
      <div className="storage-header">
        <div>
          <h2 className="storage-title">Storage Objects</h2>
          <h6 className="storage-subtitle">{storageObjects.length} objects found.</h6>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleRefresh}
          disabled={loading}
          title="Refresh storage objects"
        >
          {loading ? <LoadingSpinner /> : 'Refresh'}
        </button>
      </div>

      <div className="storage-controls">
        <div className="search-section">
          <form onSubmit={handleSubmit} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                className="search-input"
                value={searchFilters.collection || ''}
                onChange={(e) => handleFilterChange('collection', e.target.value)}
                placeholder="Filter by collection"
              />
              <input
                type="text"
                className="search-input"
                value={searchFilters.key || ''}
                onChange={(e) => handleFilterChange('key', e.target.value)}
                placeholder="Filter by key (use '%' for wildcard search)"
              />
              <input
                type="text"
                className="search-input"
                value={searchFilters.user_id || ''}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                placeholder="Filter by user ID"
              />
              <div className="search-buttons">
                <button
                  type="submit"
                  className="btn btn-primary search-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      <span className="ms-2">Searching...</span>
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="pagination-section">
          <div className="pagination-buttons">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(0)}
              disabled={storageObjects.length === 0 || loading}
              title="First page"
            >
              <PageFirstIcon />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(1)}
              disabled={!nextCursor || loading}
              title="Next page"
            >
              <PageNextIcon />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Error when querying storage objects:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading storage objects...</strong>
        </div>
      )}

      <div className="storage-table-container">
        <table className="storage-table">
          <thead>
            <tr>
              <th>Collection</th>
              <th>Key</th>
              <th style={{ width: '280px' }}>User ID</th>
              <th>Read Permission</th>
              <th>Write Permission</th>
              <th style={{ width: '180px' }}>Last Update</th>
              {deleteAllowed() && <th style={{ width: '90px' }}>Remove</th>}
            </tr>
          </thead>
          <tbody>
            {storageObjects.length === 0 && !loading && (
              <tr>
                <td colSpan={deleteAllowed() ? 7 : 6} className="no-storage-objects">
                  No storage objects found - change the filter criteria or add new storage objects.
                </td>
              </tr>
            )}
            {storageObjects.map((storageObject, index) => (
              <tr key={`${storageObject.collection}-${storageObject.key}-${storageObject.user_id}`} className="storage-row">
                <td onClick={() => handleViewStorageObject(storageObject)} className="clickable">
                  {storageObject.collection}
                </td>
                <td onClick={() => handleViewStorageObject(storageObject)} className="clickable">
                  {storageObject.key}
                </td>
                <td onClick={() => handleViewStorageObject(storageObject)} className="clickable">
                  {storageObject.user_id}
                </td>
                <td onClick={() => handleViewStorageObject(storageObject)} className="clickable">
                  {getPermissionText(storageObject.permission_read)}
                </td>
                <td onClick={() => handleViewStorageObject(storageObject)} className="clickable">
                  {getPermissionText(storageObject.permission_write, true)}
                </td>
                <td onClick={() => handleViewStorageObject(storageObject)} className="clickable">
                  {formatDate(storageObject.update_time)}
                </td>
                {deleteAllowed() && (
                  <td className="delete-cell">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteStorageObject(storageObject, index)}
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default Storage;
